import { ReactNode, HTMLAttributes } from "react";
import "./summary-card-view.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormattedMessage, defineMessage } from "react-intl";
import TooltipTrigger from "../../tooltip-trigger";

/**
 * Label that actual summary cards can use to unify the "total" count of items label they inject.
 */
export const TotalLabel = defineMessage({
  defaultMessage: "Total",
  description: "Label for total count of items in summary cards",
});

//<editor-fold desc="Props">

interface SummaryCardDOMProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {}
export interface SummaryCardProps extends SummaryCardDOMProps {
  isReady?: boolean;
  title: ReactNode;
  items?: ReactNode[];
  itemsButton?: {
    label: (n: number) => string;
    icon?: ReactNode;
    action: () => void;
  };
  headerButton?: { label: string; icon?: ReactNode; action: () => void };
  onReloadData?: () => void;
  footer?: ReactNode;
}

function SummaryCard(props: SummaryCardProps) {
  const {
    title,
    items,
    itemsButton,
    footer,
    children,
    headerButton,
    isReady,
    onReloadData,
    ...other
  } = props;
  return (
    <div className={"card content-section summary-card"} {...other}>
      <div className={"card-header clearfix"}>
        <h2 className={"mb-0 d-inline-block"}>{title}</h2>
        {onReloadData && (
          <TooltipTrigger
            tipKey={"toolsReloadTip"}
            tip={
              <FormattedMessage
                defaultMessage="Reload data"
                description={"tooltip for reload data button"}
              />
            }
          >
            <button
              data-test-summary-card-reload-trigger
              className={"btn btn-sm btn-theme-to-primary float-right ml-1"}
              type={"button"}
              onClick={onReloadData}
            >
              <span className="fa-layers fa-fw">
                <FontAwesomeIcon
                  icon="circle"
                  mask={"database"}
                  className={"opacity-50"}
                  transform="shrink-0 right-5 down-5"
                />
                <FontAwesomeIcon
                  icon="sync"
                  transform="shrink-4 right-5 down-5"
                />
              </span>
            </button>
          </TooltipTrigger>
        )}
        {headerButton && (
          <button
            data-test-summary-card-more-button
            className={"btn btn-sm btn-theme-to-primary float-right ml-1"}
            onClick={headerButton.action}
          >
            {headerButton.icon}
            <span>{headerButton.label}</span>
          </button>
        )}
      </div>
      {!isReady && (
        <>
          <div className={"card-body"}></div>
          <div className={"initializing" + (!isReady ? " show" : "")}>
            <div className={"initializing-body"}>
              <FontAwesomeIcon icon={"spinner"} spin size={"4x"} />
            </div>
          </div>
        </>
      )}
      {!!isReady && items && items.length && (
        //
        <ul className={"list-group list-group-flush"}>
          <>
            {items.slice(0, 3).map((itm: ReactNode, i) => {
              return (
                <li
                  className={
                    "list-group-item d-flex justify-content-between align-items-center"
                  }
                  key={"itm_" + i}
                >
                  {itm}
                </li>
              );
            })}
            {itemsButton && items.length > 3 && (
              <li className={"list-group-item"}>
                <button
                  className={"btn btn-theme-to-secondary btn-block btn-sm"}
                  onClick={itemsButton.action}
                >
                  {itemsButton.label(items.length - 3)}
                </button>
              </li>
            )}
          </>
        </ul>
      )}
      {children && <div className={"card-body"}>{!!isReady && children}</div>}
      {footer && (
        <div className={"card-footer small text-muted"}>
          {!!isReady && footer}
        </div>
      )}
    </div>
  );
}
export default SummaryCard;
